// Envíos > Lista legacy: tabla operativa de solo lectura sobre
// legacy.envios (la fuente que puebla el poller de Lightdata). Deliberadamente
// simple — sin dashboard, sin cards, sin métricas — solo una tabla paginada,
// ordenable y filtrable, parecida a la tabla legacy de referencia.
//
// No toca ni reemplaza la Lista de envíos actual (Deliveries.tsx,
// public.deliveries): es una fuente y una pantalla completamente aparte.
import React, { useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import EnvioLegacyDetailModal from "../components/envios/EnvioLegacyDetailModal";

type EnvioLegacyRow = {
  lightdata_id: number | string | null;
  numero_tracking: string | null;
  nombre_fantasia: string | null;
  id_venta_ml: string | null;
  origen: string | null;
  fecha_venta: string | null;
  fecha_estado: string | null;
  fecha_estado_ar: string | null;
  nombre_destinatario: string | null;
  cp: string | null;
  zona_entrega: string | null;
  zonas_costos: string | null;
  cadete: string | null;
  estado: string | null;
  qr_svg: string | null;
};

type SortDir = "asc" | "desc";

type Column = {
  key: string;
  label: string;
  sortable: boolean;
  render: (row: EnvioLegacyRow) => React.ReactNode;
};

const PAGE_SIZE = 50;

const pageWrapStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: "16px" };
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "26px", fontWeight: 800, color: "var(--text-primary)", letterSpacing: "-0.02em" };
const subtitleStyle: React.CSSProperties = { margin: "6px 0 0 0", color: "var(--text-secondary)", fontSize: "14px" };
const filtersRowStyle: React.CSSProperties = { display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end" };
const filterFieldStyle: React.CSSProperties = { minWidth: "160px", flex: "1 1 160px" };
const searchFieldStyle: React.CSSProperties = { minWidth: "240px", flex: "2 1 240px" };
const tableWrapStyle: React.CSSProperties = { overflowX: "auto", border: "1px solid var(--border)", borderRadius: "18px" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse" };
const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 10px",
  color: "var(--text-secondary)",
  fontSize: "12px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
  cursor: "pointer",
  userSelect: "none",
  whiteSpace: "nowrap",
  background: "rgba(255,255,255,0.02)",
};
const thStaticStyle: React.CSSProperties = { ...thStyle, cursor: "default" };
const trClickableStyle: React.CSSProperties = { cursor: "pointer" };
const tdStyle: React.CSSProperties = {
  padding: "8px 10px",
  color: "var(--text-primary)",
  fontSize: "13px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};
const emptyStyle: React.CSSProperties = { padding: "24px 12px", color: "var(--text-secondary)", fontSize: "14px", textAlign: "center" };
const footerRowStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" };
const pagerButtonStyle: React.CSSProperties = {
  padding: "6px 12px",
  borderRadius: "8px",
  border: "1px solid var(--border)",
  background: "transparent",
  color: "var(--text-primary)",
  cursor: "pointer",
  fontSize: "13px",
};
const pagerButtonDisabledStyle: React.CSSProperties = { ...pagerButtonStyle, opacity: 0.4, cursor: "not-allowed" };

const ESTADO_COLOR: Record<string, string> = {
  entregado: "var(--success, #2ea043)",
  cancelado: "var(--danger, #d1242f)",
  "no entregado": "var(--danger, #d1242f)",
  "rechazado por el comprador": "var(--danger, #d1242f)",
};

function estadoBadgeColor(estado: string | null): string {
  if (!estado) return "var(--text-secondary)";
  return ESTADO_COLOR[estado.trim().toLowerCase()] || "var(--warning, #d29922)";
}

const badgeStyle = (color: string): React.CSSProperties => ({
  display: "inline-block",
  padding: "2px 9px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 600,
  border: `1px solid ${color}`,
  color,
  whiteSpace: "nowrap",
});

export default function EnviosLegacy() {
  const { token } = useAuth();

  const [items, setItems] = useState<EnvioLegacyRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [estado, setEstado] = useState("");
  const [cliente, setCliente] = useState("");
  const [chofer, setChofer] = useState("");
  const [origen, setOrigen] = useState("");

  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const [selectedId, setSelectedId] = useState<number | string | null>(null);

  // Debounce simple: espera a que el usuario deje de tipear antes de
  // disparar la búsqueda contra el backend (legacy.envios no tiene índices
  // en las columnas de texto, así que no conviene una request por tecla).
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (search.trim()) params.set("search", search.trim());
    if (estado.trim()) params.set("estado", estado.trim());
    if (cliente.trim()) params.set("cliente", cliente.trim());
    if (chofer.trim()) params.set("chofer", chofer.trim());
    if (origen.trim()) params.set("origen", origen.trim());
    if (sortBy) {
      params.set("sortBy", sortBy);
      params.set("sortDir", sortDir);
    }

    authFetch(`/v1/envios-legacy?${params.toString()}`, { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "Error al cargar el listado legacy");
        if (cancelled) return;
        setItems(payload.items ?? []);
        setTotalPages(payload.totalPages ?? 1);
        setTotal(payload.total ?? 0);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, page, search, estado, cliente, chofer, origen, sortBy, sortDir]);

  const handleHeaderClick = (column: Column) => {
    if (!column.sortable) return;
    if (sortBy !== column.key) {
      setSortBy(column.key);
      setSortDir("desc");
    } else {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    }
    setPage(1);
  };

  const columns: Column[] = useMemo(
    () => [
      {
        key: "qr",
        label: "QR",
        sortable: false,
        render: (r) => (r.qr_svg ? <span style={{ width: 28, height: 28, display: "inline-block" }} dangerouslySetInnerHTML={{ __html: r.qr_svg }} /> : "—"),
      },
      { key: "nombre_fantasia", label: "Nombre fantasía", sortable: true, render: (r) => r.nombre_fantasia || "—" },
      { key: "id_venta_ml", label: "IDML", sortable: true, render: (r) => r.id_venta_ml || "—" },
      { key: "origen", label: "Origen", sortable: true, render: (r) => r.origen || "—" },
      { key: "numero_tracking", label: "Tracking number", sortable: true, render: (r) => r.numero_tracking || "—" },
      { key: "fecha_venta", label: "Fecha venta", sortable: true, render: (r) => r.fecha_venta || "—" },
      { key: "fecha_estado", label: "Fecha estado", sortable: true, render: (r) => r.fecha_estado_ar || "—" },
      { key: "nombre_destinatario", label: "Destino nombre", sortable: true, render: (r) => r.nombre_destinatario || "—" },
      { key: "cp", label: "Destino CP", sortable: true, render: (r) => r.cp || "—" },
      { key: "zona_entrega", label: "Zona entrega", sortable: true, render: (r) => r.zona_entrega || "—" },
      { key: "zonas_costos", label: "Zona costo", sortable: true, render: (r) => r.zonas_costos || "—" },
      { key: "cadete", label: "Chofer", sortable: true, render: (r) => r.cadete || "—" },
      {
        key: "estado",
        label: "Estado",
        sortable: true,
        render: (r) => (r.estado ? <span style={badgeStyle(estadoBadgeColor(r.estado))}>{r.estado}</span> : "—"),
      },
    ],
    []
  );

  return (
    <div style={pageWrapStyle}>
      <div>
        <h1 style={titleStyle}>Envíos — Lista legacy</h1>
        <p style={subtitleStyle}>
          Fuente: legacy.envios (Lightdata). Tabla operativa de solo lectura — no reemplaza la Lista de envíos actual.
        </p>
      </div>

      <Card padding="14px">
        <div style={filtersRowStyle}>
          <div style={searchFieldStyle}>
            <Input
              label="Buscar"
              placeholder="Tracking, cliente, destinatario, chofer…"
              value={searchInput}
              onChange={(e: any) => setSearchInput(e.target.value)}
            />
          </div>
          <div style={filterFieldStyle}>
            <Input
              label="Estado"
              placeholder="Entregado, En camino…"
              value={estado}
              onChange={(e: any) => { setEstado(e.target.value); setPage(1); }}
            />
          </div>
          <div style={filterFieldStyle}>
            <Input
              label="Cliente"
              placeholder="Nombre fantasía"
              value={cliente}
              onChange={(e: any) => { setCliente(e.target.value); setPage(1); }}
            />
          </div>
          <div style={filterFieldStyle}>
            <Input
              label="Chofer"
              placeholder="Cadete"
              value={chofer}
              onChange={(e: any) => { setChofer(e.target.value); setPage(1); }}
            />
          </div>
          <div style={filterFieldStyle}>
            <Input
              label="Origen"
              placeholder="MercadoLibre, TN, Directo…"
              value={origen}
              onChange={(e: any) => { setOrigen(e.target.value); setPage(1); }}
            />
          </div>
        </div>
      </Card>

      {error ? (
        <Card padding="14px" style={{ color: "var(--danger, #d1242f)" }}>
          {error}
        </Card>
      ) : null}

      <Card padding="12px">
        <div style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    style={column.sortable ? thStyle : thStaticStyle}
                    onClick={() => handleHeaderClick(column)}
                  >
                    {column.label}
                    {column.sortable && sortBy === column.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 && (
                <tr>
                  <td style={emptyStyle} colSpan={columns.length}>
                    {total === 0 ? "No hay envíos legacy sincronizados todavía." : "Nada para estos filtros."}
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td style={emptyStyle} colSpan={columns.length}>
                    Cargando…
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((row, index) => (
                  <tr
                    key={row.lightdata_id != null ? String(row.lightdata_id) : `${row.numero_tracking}-${index}`}
                    style={row.lightdata_id != null ? trClickableStyle : undefined}
                    onClick={() => row.lightdata_id != null && setSelectedId(row.lightdata_id)}
                    title={row.lightdata_id != null ? "Ver detalle" : undefined}
                  >
                    {columns.map((column) => (
                      <td key={column.key} style={tdStyle}>
                        {column.render(row)}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div style={{ ...footerRowStyle, marginTop: "12px" }}>
          <span style={{ color: "var(--text-secondary)", fontSize: "13px" }}>
            {total.toLocaleString("es-AR")} envío{total === 1 ? "" : "s"} · página {page} de {totalPages}
          </span>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              style={page <= 1 ? pagerButtonDisabledStyle : pagerButtonStyle}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Anterior
            </button>
            <button
              type="button"
              style={page >= totalPages ? pagerButtonDisabledStyle : pagerButtonStyle}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Siguiente →
            </button>
          </div>
        </div>
      </Card>

      <EnvioLegacyDetailModal
        open={selectedId != null}
        lightdataId={selectedId}
        token={token}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
